import React from "react";
import { Link } from "react-router-dom";
import { Badge, Skeleton } from "@radix-ui/themes";
import { ArrowRight, CircleAlert, Plug, Plus, RefreshCw } from "lucide-react";
import { useProfileContext } from "context";
import comoLogo from "../../assets/images/como.svg";
import {
  useGetActiveIntegrations, useGetAirshipStatus, useGetMetaStatus,
  useGetAtreemoStatus, useGetKlaviyoStatus, useGetGoogleAdsStatus,
  useGetComoStatus, useGetBrevoStatus, useGetDotdigitalStatus,
  useGetWhatsappCredentialsStatus, useGetLeatStatus, useGetSevenRoomsStatus,
  useGetBrazeStatus, useGetEatAppStatus, useGetGoogleReviewsStatus, useGetTripadvisorReviewsStatus,
} from "hooks";
import "./HomeIntegrations.css";

const TOOLS = [
  ["google-reviews", "Google Reviews", "Reviews & reputation", require("../../assets/images/google.png")],
  ["tripadvisor-reviews", "Tripadvisor", "Reviews & reputation", require("../../assets/images/tripadvisor.png")],
  ["bconnect", "b connect Wi-Fi", "Guest Wi-Fi", require("../../assets/images/favicon-light.png")],
  ["unifi", "UniFi", "Guest Wi-Fi", require("../../assets/images/unifi.png")],
  ["cisco", "Cisco Meraki", "Wi-Fi & presence", require("../../assets/images/cisco.png")],
  ["extreme", "Extreme Networks", "Guest Wi-Fi", require("../../assets/images/extreme.png")],
  ["tplink", "TP-Link Omada", "Guest Wi-Fi", require("../../assets/images/tp-link-omada.png")],
  ["cambium", "Cambium Networks", "Guest Wi-Fi", require("../../assets/images/cambium-networks.png")],
  ["mailchimp", "Mailchimp", "Email marketing", require("../../assets/images/mailchimp.png")],
  ["airship", "Airship", "CRM & marketing", require("../../assets/images/airship.png")],
  ["meta", "Meta", "Advertising", require("../../assets/images/meta.png")],
  ["atreemo", "Atreemo", "CRM & marketing", require("../../assets/images/atreemo.jpg")],
  ["klaviyo", "Klaviyo", "CRM & marketing", require("../../assets/images/klaviyo.jpg")],
  ["google-ads", "Google Ads", "Advertising", require("../../assets/images/google.png")],
  ["como", "Como", "Loyalty & marketing", comoLogo],
  ["brevo", "Brevo", "CRM & marketing", require("../../assets/images/brevo.jpg")],
  ["dotdigital", "Dotdigital", "CRM & marketing", require("../../assets/images/dotdigtal.png")],
  ["whatsapp", "WhatsApp", "Guest messaging", require("../../assets/images/whatsapp.png")],
  ["leat", "Leat", "Loyalty & marketing", require("../../assets/images/leat.jpeg")],
  ["sevenrooms", "SevenRooms", "Guest relationships", require("../../assets/images/sevenrooms.jpeg")],
  ["braze", "Braze", "CRM & marketing", require("../../assets/images/braze.png")],
  ["eat-app", "Eat App", "Restaurant guest CRM", null],
].map(([id, name, category, logo]) => ({ id, name, category, logo }));

function ToolLogo({ tool }) {
  return <span className="home-integration-logo" aria-hidden="true">
    {tool.id === "eat-app" ? <span className="home-integration-eat-app-mark">eat</span> : <img src={tool.logo} alt="" />}
  </span>;
}

export default function HomeIntegrations() {
  const { userProfile } = useProfileContext();
  const organisationId = userProfile?.organisation_id;
  // Reuse the same status queries and cache as the Integrations page. The
  // legacy active-integrations endpoint only includes Wi-Fi and Mailchimp.
  const legacy = useGetActiveIntegrations(organisationId);
  const statuses = {
    airship: useGetAirshipStatus(organisationId),
    meta: useGetMetaStatus(organisationId),
    atreemo: useGetAtreemoStatus(organisationId),
    klaviyo: useGetKlaviyoStatus(organisationId),
    "google-ads": useGetGoogleAdsStatus(organisationId),
    como: useGetComoStatus(organisationId),
    brevo: useGetBrevoStatus(organisationId),
    dotdigital: useGetDotdigitalStatus(organisationId),
    whatsapp: useGetWhatsappCredentialsStatus(organisationId),
    leat: useGetLeatStatus(organisationId),
    sevenrooms: useGetSevenRoomsStatus(organisationId),
    braze: useGetBrazeStatus(organisationId),
    "eat-app": useGetEatAppStatus(organisationId),
    "google-reviews": useGetGoogleReviewsStatus(organisationId),
    "tripadvisor-reviews": useGetTripadvisorReviewsStatus(organisationId),
  };
  const queries = [legacy, ...Object.values(statuses)];
  const loading = !organisationId || queries.some((query) => query.isPending);
  // Several optional integration APIs use 404 to mean "no configuration".
  // That is a disconnected state, not a status-loading failure.
  const failed = queries.filter((query) => (
    query.isError && query.error?.response?.status !== 404
  ));
  const legacyIds = new Set(legacy.isError ? [] : (legacy.data?.data?.integrations || []).map((item) => item.name));
  const connected = TOOLS.filter(({ id }) => {
    const query = statuses[id];
    if (!query) return legacyIds.has(id);
    if (query.isError) return false;
    return id.endsWith("-reviews") ? query.data?.data?.status === "active" : query.data?.data?.is_active === true;
  });

  return (
    <section className="home-integrations" aria-labelledby="home-integrations-title" aria-busy={loading}>
      <div className="home-section-heading">
        <div>
          <h2 id="home-integrations-title"><Plug size={19} strokeWidth={1.7} /> Your connected tools
            {!loading && failed.length === 0 && connected.length > 0 && <Badge color="gray" variant="soft">{connected.length} connected</Badge>}
          </h2>
          <p>Bring your guest experience together, one connection at a time.</p>
        </div>
        <Link className="home-text-link" to="/integrations/active">Manage integrations <ArrowRight size={15} /></Link>
      </div>

      {failed.length > 0 && <div className="home-integrations-error" role="status">
        <CircleAlert size={15} /><span>Some connection statuses couldn’t be loaded.</span>
        <button className="home-retry" type="button" onClick={() => failed.forEach((query) => query.refetch())}><RefreshCw size={13} /> Retry integrations</button>
      </div>}

      {loading && connected.length === 0 ? (
        <div className="home-integrations-grid" role="status" aria-label="Loading integrations">
          {[0, 1, 2].map((index) => <Skeleton key={index}><div className="home-integration-loading" /></Skeleton>)}
        </div>
      ) : connected.length > 0 ? (
        <div className="home-integrations-grid">
          {connected.slice(0, 5).map((tool) => <Link className="home-integration-tile" to="/integrations/active" key={tool.id} aria-label={`Manage ${tool.name}`}>
            <ToolLogo tool={tool} />
            <span className="home-integration-info"><strong>{tool.name}</strong><span>{tool.category}</span><span className="home-integration-status"><span /> Connected</span></span>
            <ArrowRight className="home-integration-arrow" size={16} />
          </Link>)}
          <Link className="home-integration-discover" to="/integrations/browse-all">
            <span className="home-integration-add"><Plus size={21} /></span>
            <span><strong>Make another connection</strong><span>Explore more tools for your business</span></span><ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="home-integrations-empty">
          <div className="home-integrations-logo-stack" aria-hidden="true">
            {["unifi", "google-reviews", "mailchimp"].map((id) => <ToolLogo tool={TOOLS.find((tool) => tool.id === id)} key={id} />)}
          </div>
          <div><h3>{failed.length ? "Find your next connection" : "Your favourite tools. Better together."}</h3><p>Connect your Wi-Fi, review platforms and marketing tools to get more from Bconnect.</p></div>
          <Link className="home-integrations-browse" to="/integrations/browse-all">Browse integrations <ArrowRight size={16} /></Link>
        </div>
      )}
      {connected.length > 5 && <Link className="home-integrations-more" to="/integrations/active">View {connected.length - 5} more connected {connected.length - 5 === 1 ? "tool" : "tools"} <ArrowRight size={13} /></Link>}
    </section>
  );
}
